import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";
import type { CSSProperties } from "react";
import * as api from "../lib/api.ts";
import { hueClass, nextTemplate } from "../lib/hue.ts";
import { toIsoDate } from "@training/shared";
import { musclesLine, restingMuscles, suggestionReason } from "../lib/journal.ts";
import { sinceLabel } from "../lib/session.ts";

/**
 * The cycle's sessions to start from, the one due next cast in its colour.
 * The same list on the page and in the sheet the middle button opens.
 *
 * The coach plans in six week blocks: "Séance 1/2/3" stay the same for the
 * whole cycle and the phone picks one. The only thing that decides which is
 * how long ago each was last done, so that is the one number on each card.
 *
 * A session done this week is locked, unless asked for one more: a fourth
 * session in the week is one of the three again.
 */
export function SessionChoices({ cycle, onStarted }: { cycle: api.Cycle; onStarted?: () => void }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const current = useQuery({ queryKey: ["session", "current"], queryFn: api.getCurrentSession });
  const ongoing = current.data?.session?.status === "in_progress" ? current.data.session : null;
  const journal = useQuery({ queryKey: ["journal", null], queryFn: () => api.getJournal() });
  const resting = journal.data ? restingMuscles(journal.data.sessions, toIsoDate()) : [];
  const [extra, setExtra] = useState(false);

  const start = useMutation({
    mutationFn: api.startTemplate,
    onSuccess: async ({ session }) => {
      await client.invalidateQueries({ queryKey: ["session"] });
      onStarted?.();
      navigate(`/seance/${session.id}`);
    },
  });

  const data = cycle;
  // With a session open, that one leads: nothing else is "next" yet.
  const next = ongoing ? null : nextTemplate(data);

  return (
    <>
      <ul className="grid gap-3 lg:grid-cols-3">
        {data.templates.map((template, index) => {
          const suggested = template.id === next?.id;
          // Said on the one to do: the reason it is that one, not another.
          const reason = suggested ? suggestionReason(template.muscles, resting) : null;
          const locked = template.doneThisWeek && !extra;
          return (
            <li
              key={template.id}
              className={`animate-rise flex min-w-0 flex-col ${hueClass(template.position)}`}
              style={{ "--i": index } as CSSProperties}
            >
              {/* Done this week cannot be started again by mistake: Jambes
                  was once started a second time while Pecs was still to do. */}
              <button
                type="button"
                disabled={start.isPending || locked}
                onClick={() => start.mutate(template.id)}
                className={[
                  "group flex w-full flex-1 items-center gap-4 rounded-card px-4 text-left",
                  "lg:flex-col lg:items-start lg:gap-2 lg:px-5 lg:py-5",
                  // The one to do next is cast in its colour; the others only carry it.
                  suggested
                    ? "slab py-6"
                    : locked
                      ? "card py-4 opacity-60"
                      : "card py-4 transition-transform active:scale-[0.985]",
                ].join(" ")}
              >
                <span className="min-w-0 flex-1 lg:w-full">
                  {suggested && (
                    <span className="block text-[0.8rem] font-semibold opacity-70">
                      À faire ensuite{reason && ` · ${reason}`}
                    </span>
                  )}
                  <span
                    className={[
                      "wide block truncate font-bold",
                      suggested ? "text-[1.35rem] lg:text-[1.5rem]" : "text-[1.15rem] lg:text-[1.3rem]",
                    ].join(" ")}
                  >
                    {template.name}
                  </span>
                  <span
                    className={["num mt-0.5 block text-[0.84rem]", suggested ? "opacity-75" : "text-ink-mute"].join(
                      " ",
                    )}
                  >
                    {/* When it was last done is what picks it: the count of
                        movements decided nothing. */}
                    {template.doneThisWeek && (
                      <span className="font-semibold text-ink-soft">Faite cette semaine · </span>
                    )}
                    {sinceLabel(template.lastPerformedOn)}
                  </span>
                  {template.muscles.length > 0 && (
                    <span
                      className={["block truncate text-[0.84rem]", suggested ? "opacity-75" : "text-ink-soft"].join(
                        " ",
                      )}
                    >
                      {musclesLine(template.muscles)}
                    </span>
                  )}
                </span>

                {!locked && (
                  <ArrowRight
                    size={20}
                    strokeWidth={suggested ? 2.5 : 2}
                    className={[
                      "shrink-0 transition-transform group-hover:translate-x-0.5 lg:hidden",
                      suggested ? "" : "text-ink-mute",
                    ].join(" ")}
                    aria-hidden
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {!ongoing && data.templates.some((template) => template.doneThisWeek) && (
        <button
          type="button"
          aria-pressed={extra}
          onClick={() => setExtra((current) => !current)}
          className="mt-3 flex h-11 w-full items-center justify-center rounded-card text-[0.88rem] font-semibold text-ink-soft active:bg-surface-high"
        >
          {extra ? "Revenir aux séances à faire" : "Une séance en plus cette semaine"}
        </button>
      )}

      {start.isError && (
        <p className="mt-4 text-center text-[0.85rem] text-stop">Impossible de démarrer la séance. Réessayez.</p>
      )}
    </>
  );
}
